import React, { useEffect, useState } from 'react'
import { OrganizationsHeader, PeopleFilters, PeopleTable, type PersonFilters } from 'components'
import { Box, CircularProgress, Pagination, Typography, Grid } from '@mui/material'
import { useSessionContext } from 'session'
import {
  addMissingValueInParams,
  type Person,
  type PersonFilterRequest,
  type PersonFilterResults,
  personListFilter
} from 'services'

export const PeoplePage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [people, setPeople] = useState<Person[]>([])
  const initialState: PersonFilters = {
    first_name: '',
    last_name: '',
    id: '',
    uniqueId: '',
    countries: [],
    page: 1,
    order_field: 'id',
    order_direction: 'ASC',
    missing_value: undefined,
    email: '',
    positionTypes: []
  }
  const [filters, setFilters] = useState<PersonFilters>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<PersonFilters>(initialState)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    changeModuleName('People')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) {
      setLoadResultWithFilters(prevState => !prevState)
    }
  }

  const fetchPeople = async (): Promise<PersonFilterResults> => {
    setLoading(true)

    try {
      const params: PersonFilterRequest = {
        id: filters.id !== '' ? filters.id : undefined,
        uniqueid: filters.uniqueId !== '' ? filters.uniqueId : undefined,
        first_name: filters.first_name !== '' ? filters.first_name.trim() : undefined,
        last_name: filters.last_name !== '' ? filters.last_name.trim() : undefined,
        country: filters.countries.map(value => value.id),
        page: filters.page !== 0 ? filters.page : undefined,
        order_by: filters.order_field,
        order_direction: filters.order_direction,
        email: filters.email !== '' ? filters.email?.trim().toLocaleLowerCase() : undefined,
        positionTypeIds: filters.positionTypes.map((positionType) => positionType.id)
      }
      if (filters.missing_value != null) {
        addMissingValueInParams(params, filters.missing_value)
      }
      return await personListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateTablePeopleWithFilter = async (): Promise<void> => {
    fetchPeople().then(response => {
      setPeople(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
    }).catch(reason => {
      console.log(reason)
      setPeople([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updateTablePeopleWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  function updateOrdering (field: string, direction: string): void {
    updateFilters({ order_field: field, order_direction: direction })
    setLoadResultWithFilters(prevState => !prevState)
  }

  return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='95%' sx={{ pb: 1 }}>
                    <OrganizationsHeader/>
                    <PeopleFilters doFilter={updateTablePeopleWithFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilter}/>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {loading && <CircularProgress sx={{ alignSelf: 'center', mb: 3 }}/>}
                    {!loading && <PeopleTable people={people} orderField={filters.order_field} orderDirection={filters.order_direction} updateOrdering={updateOrdering}/>}
                    </Box>
                </Box>
                <Grid container justifyContent="space-between">
                  <Grid item xs/>
                  <Grid item>
                    <Pagination page={filters.page} count={pageCount} onChange={(event, page) => {
                      updateFilters({ page })
                      setLoadResultWithFilters(prevState => !prevState)
                    }}/>
                  </Grid>
                  <Grid item xs display="flex" justifyContent="flex-end">
                    <Typography variant="body1" sx={{ mr: 2 }}>
                      Page:<strong>{people.length}</strong> Total:<strong>{totalCount}</strong>
                    </Typography>
                  </Grid>
                </Grid>
            </Box>
        </Box>
  )
}
